import React, { useEffect, useState } from "react";
import { Plus, Edit2, Trash2, Upload, X, Image as ImageIcon, Eye, EyeOff, ExternalLink as LinkIcon } from "lucide-react";
import { api, fmtPrice, BACKEND_URL } from "../../lib/api";
import { toast } from "sonner";
import { PLATFORM_OPTIONS } from "../../components/BuyButton";

const emptyForm = {
    name: "", slug: "", price: 0, original_price: null, category_slug: "vestuario",
    description: "", short_description: "", images: [], sizes: [], colors: [],
    video_url: "", mercado_livre_url: "", external_links: [],
    rating: 4.8, reviews_count: 0,
    stock_status: "Disponível para compra online", badges: [],
    is_featured: false, is_bestseller: false, is_new: false, is_active: true,
};

const input = "w-full border border-brand-border focus:border-brand-ink outline-none px-3 py-2.5 text-sm rounded-sm bg-white transition-colors";

const Field = ({ label, hint, children }) => (
    <div>
        <label className="block text-xs font-bold uppercase tracking-wider text-brand-ink mb-1.5">{label}</label>
        {children}
        {hint && <p className="text-[11px] text-brand-inkSoft mt-1">{hint}</p>}
    </div>
);

const Modal = ({ open, onClose, title, children }) => {
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4" onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()} className="bg-white w-full sm:max-w-3xl max-h-[92vh] overflow-y-auto rounded-t-lg sm:rounded-sm shadow-2xl">
                <div className="sticky top-0 bg-white border-b border-brand-border px-6 py-4 flex items-center justify-between z-10">
                    <h2 className="font-heading font-bold text-lg text-brand-ink">{title}</h2>
                    <button onClick={onClose} className="w-9 h-9 hover:bg-brand-bg rounded-sm flex items-center justify-center"><X className="w-4 h-4" /></button>
                </div>
                <div className="p-6">{children}</div>
            </div>
        </div>
    );
};

const slugify = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const ProductForm = ({ initial, categories, onSubmit, onCancel }) => {
    const initLinks = (() => {
        if (initial?.external_links?.length) return initial.external_links;
        if (initial?.mercado_livre_url) return [{ platform: "Mercado Livre", url: initial.mercado_livre_url, is_active: true }];
        return [];
    })();
    const [form, setForm] = useState({ ...emptyForm, ...initial, external_links: initLinks });
    const [uploading, setUploading] = useState(false);
    const [sizesInput, setSizesInput] = useState(initial?.sizes?.join(", ") || "");
    const [colorName, setColorName] = useState("");
    const [colorHex, setColorHex] = useState("#1A1A1A");
    const [newPlatform, setNewPlatform] = useState("Mercado Livre");
    const [newUrl, setNewUrl] = useState("");
    const [customPlatform, setCustomPlatform] = useState("");

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const upload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 10 * 1024 * 1024) {
            toast.error("Imagem muito grande (máx 10MB). Reduza a resolução e tente de novo.");
            e.target.value = "";
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/admin/upload", fd);
            update("images", [...(form.images || []), `${BACKEND_URL}${data.url}`]);
            if (data.saved_pct > 5) {
                toast.success(`Imagem otimizada (${data.saved_pct}% menor)`);
            } else {
                toast.success("Imagem enviada");
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || "Falha no upload — verifique sua conexão e tente de novo.");
        }
        setUploading(false);
        e.target.value = "";
    };

    const [uploadingVideo, setUploadingVideo] = useState(false);
    const uploadVideo = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 100 * 1024 * 1024) {
            toast.error("Vídeo muito grande (máx 100MB). Use uma URL do YouTube/Instagram para vídeos maiores.");
            e.target.value = "";
            return;
        }
        setUploadingVideo(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/admin/upload", fd);
            update("video_url", `${BACKEND_URL}${data.url}`);
            toast.success("Vídeo do produto enviado");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Falha no upload do vídeo");
        }
        setUploadingVideo(false);
        e.target.value = "";
    };

    const addUrl = () => {
        const u = window.prompt("Cole a URL da imagem:");
        if (u) update("images", [...(form.images || []), u]);
    };
    const removeImg = (i) => update("images", form.images.filter((_, x) => x !== i));
    const addColor = () => { if (!colorName) return; update("colors", [...(form.colors || []), { name: colorName, hex: colorHex }]); setColorName(""); };
    const removeColor = (i) => update("colors", form.colors.filter((_, x) => x !== i));

    const addLink = () => {
        if (!newUrl) { toast.error("Cole a URL"); return; }
        const platform = newPlatform === "Outra" ? (customPlatform.trim().slice(0, 40) || "Outra") : newPlatform;
        update("external_links", [...(form.external_links || []), { platform, url: newUrl.trim(), is_active: true }]);
        setNewUrl("");
        setCustomPlatform("");
    };
    const removeLink = (i) => update("external_links", form.external_links.filter((_, x) => x !== i));
    const toggleLink = (i) => update("external_links", form.external_links.map((l, x) => x === i ? { ...l, is_active: !l.is_active } : l));
    const setAllLinksActive = (active) => update("external_links", (form.external_links || []).map((l) => ({ ...l, is_active: active })));

    const submit = (e) => {
        e.preventDefault();
        if (!form.external_links || form.external_links.length === 0) {
            toast.error("Adicione pelo menos 1 link de plataforma (Mercado Livre, Shopee, etc.)");
            return;
        }
        if (!form.external_links.some((l) => l.is_active)) {
            toast.error("Pelo menos 1 plataforma precisa estar ATIVA — senão o botão 'Comprar' fica indisponível.");
            return;
        }
        onSubmit({
            ...form,
            price: parseFloat(form.price) || 0,
            original_price: form.original_price ? parseFloat(form.original_price) : null,
            rating: parseFloat(form.rating) || 4.8,
            reviews_count: parseInt(form.reviews_count) || 0,
            sizes: sizesInput.split(",").map((s) => s.trim()).filter(Boolean),
            badges: typeof form.badges === "string" ? form.badges.split(",").map((s) => s.trim()).filter(Boolean) : form.badges,
            // mantém mercado_livre_url como espelho do 1º ML link para retrocompatibilidade
            mercado_livre_url: (form.external_links.find((l) => l.platform === "Mercado Livre") || {}).url || "",
        });
    };

    return (
        <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Nome"><input data-testid="form-name" required className={input} value={form.name} onChange={(e) => { update("name", e.target.value); if (!initial?.id) update("slug", slugify(e.target.value)); }} /></Field>
                <Field label="Slug (URL)"><input data-testid="form-slug" required className={input} value={form.slug} onChange={(e) => update("slug", slugify(e.target.value))} /></Field>
                <Field label="Preço (R$)"><input data-testid="form-price" type="number" step="0.01" required className={input} value={form.price} onChange={(e) => update("price", e.target.value)} /></Field>
                <Field label="Preço original (opcional)"><input data-testid="form-original-price" type="number" step="0.01" className={input} value={form.original_price || ""} onChange={(e) => update("original_price", e.target.value)} /></Field>
                <Field label="Categoria">
                    <select data-testid="form-category" className={input} value={form.category_slug} onChange={(e) => update("category_slug", e.target.value)}>
                        {categories.map((c) => <option key={c.id} value={c.slug}>{c.name}</option>)}
                    </select>
                </Field>
                <Field label="Estoque visual"><input data-testid="form-stock" className={input} value={form.stock_status} onChange={(e) => update("stock_status", e.target.value)} /></Field>
            </div>

            <Field label="Descrição curta"><input data-testid="form-short-desc" className={input} value={form.short_description} onChange={(e) => update("short_description", e.target.value)} /></Field>
            <Field label="Descrição completa"><textarea data-testid="form-description" rows={5} className={input + " resize-y"} value={form.description} onChange={(e) => update("description", e.target.value)} /></Field>

            <Field label="Imagens" hint="Faça upload do seu computador ou cole URLs.">
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                    {form.images?.map((img, i) => (
                        <div key={i} className="relative group aspect-square bg-brand-bg rounded-sm overflow-hidden border border-brand-border">
                            <img src={img} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                            <button type="button" onClick={() => removeImg(i)} className="absolute top-1 right-1 w-6 h-6 bg-black/70 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100"><X className="w-3 h-3" /></button>
                        </div>
                    ))}
                    <label className="aspect-square border-2 border-dashed border-brand-border flex flex-col items-center justify-center gap-1 cursor-pointer text-brand-inkSoft hover:border-brand-ink hover:text-brand-ink rounded-sm">
                        <Upload className="w-5 h-5" />
                        <span className="text-[10px] font-bold uppercase">{uploading ? "..." : "Upload"}</span>
                        <input type="file" accept="image/*" className="hidden" onChange={upload} data-testid="form-upload" />
                    </label>
                </div>
                <button type="button" onClick={addUrl} className="mt-2 text-xs text-brand-gold hover:underline font-semibold">+ Adicionar via URL</button>
            </Field>

            {/* Links de plataformas externas */}
            <Field label="Plataformas de compra *" hint="Adicione 1 ou mais. No site, o cliente só verá as plataformas com o toggle ATIVADO.">
                {(form.external_links || []).length > 1 && (
                    <div className="flex flex-wrap items-center gap-2 mb-3 pb-3 border-b border-brand-border">
                        <span className="text-[11px] uppercase font-bold tracking-wider text-brand-inkSoft">Em massa:</span>
                        <button
                            type="button"
                            onClick={() => setAllLinksActive(true)}
                            data-testid="form-links-enable-all"
                            className="text-xs font-bold bg-brand-success/10 text-brand-success hover:bg-brand-success hover:text-white px-3 py-1.5 rounded-sm border border-brand-success/30 transition-colors"
                        >
                            ✓ Ativar todos
                        </button>
                        <button
                            type="button"
                            onClick={() => setAllLinksActive(false)}
                            data-testid="form-links-disable-all"
                            className="text-xs font-bold bg-brand-bg text-brand-inkSoft hover:bg-brand-ink hover:text-white px-3 py-1.5 rounded-sm border border-brand-border transition-colors"
                        >
                            ✕ Desativar todos
                        </button>
                        <span className="text-[11px] text-brand-inkSoft ml-auto">
                            {(form.external_links || []).filter((l) => l.is_active).length} de {(form.external_links || []).length} ativos
                        </span>
                    </div>
                )}
                <div className="space-y-2 mb-3">
                    {(form.external_links || []).map((l, i) => (
                        <div key={i} className={`flex items-center gap-3 border rounded-sm p-2.5 transition-all ${l.is_active ? "bg-white border-brand-border" : "bg-brand-bg/60 border-brand-border opacity-70"}`} data-testid={`link-row-${i}`}>
                            <LinkIcon className={`w-4 h-4 flex-shrink-0 ${l.is_active ? "text-brand-gold" : "text-brand-inkSoft"}`} />
                            <div className="flex-1 min-w-0">
                                <div className={`font-semibold text-sm ${l.is_active ? "text-brand-ink" : "text-brand-inkSoft"}`}>{l.platform}</div>
                                <div className="text-xs text-brand-inkSoft truncate">{l.url}</div>
                            </div>
                            {/* Toggle switch visual */}
                            <button
                                type="button"
                                role="switch"
                                aria-checked={l.is_active}
                                onClick={() => toggleLink(i)}
                                data-testid={`link-toggle-${i}`}
                                title={l.is_active ? "Clique para desativar (some do site)" : "Clique para ativar (aparece no site)"}
                                className="flex items-center gap-2 flex-shrink-0"
                            >
                                <span className={`text-[10px] font-bold uppercase tracking-wider ${l.is_active ? "text-brand-success" : "text-brand-inkSoft"}`}>
                                    {l.is_active ? "ATIVO" : "INATIVO"}
                                </span>
                                <span className={`relative inline-block w-10 h-5 rounded-full transition-colors ${l.is_active ? "bg-brand-success" : "bg-brand-border"}`}>
                                    <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${l.is_active ? "translate-x-5" : ""}`} />
                                </span>
                            </button>
                            <button type="button" onClick={() => removeLink(i)} title="Remover este link" className="w-8 h-8 inline-flex items-center justify-center hover:bg-red-50 text-brand-error rounded-sm flex-shrink-0"><X className="w-4 h-4" /></button>
                        </div>
                    ))}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr_auto] gap-2 items-start">
                    <select data-testid="form-platform-select" className={input} value={newPlatform} onChange={(e) => setNewPlatform(e.target.value)}>
                        {PLATFORM_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                    <div className="space-y-2">
                        {newPlatform === "Outra" && (
                            <input className={input} placeholder="Nome da plataforma" value={customPlatform} onChange={(e) => setCustomPlatform(e.target.value)} />
                        )}
                        <input data-testid="form-platform-url" type="url" className={input} placeholder="https://..." value={newUrl} onChange={(e) => setNewUrl(e.target.value)} />
                    </div>
                    <button type="button" onClick={addLink} data-testid="form-platform-add" className="bg-brand-ink text-white hover:bg-brand-dark px-4 py-2.5 text-sm font-semibold rounded-sm">Adicionar</button>
                </div>
            </Field>

            <Field label="Tamanhos (vírgula)"><input data-testid="form-sizes" className={input} value={sizesInput} onChange={(e) => setSizesInput(e.target.value)} placeholder="PP, P, M, G, GG" /></Field>

            <Field label="Cores">
                <div className="flex flex-wrap gap-2 mb-2">
                    {form.colors?.map((c, i) => (
                        <div key={i} className="inline-flex items-center gap-2 border border-brand-border rounded-sm px-2 py-1">
                            <span className="w-4 h-4 rounded-full border-2 border-brand-ink/20 shadow-[inset_0_0_0_1px_rgba(0,0,0,0.05)]" style={{ background: c.hex }} />
                            <span className="text-xs">{c.name}</span>
                            <button type="button" onClick={() => removeColor(i)}><X className="w-3 h-3" /></button>
                        </div>
                    ))}
                </div>
                <div className="flex gap-2">
                    <input className={input + " flex-1"} placeholder="Nome da cor" value={colorName} onChange={(e) => setColorName(e.target.value)} />
                    <input type="color" className="h-11 w-14 border border-brand-border rounded-sm cursor-pointer" value={colorHex} onChange={(e) => setColorHex(e.target.value)} />
                    <button type="button" onClick={addColor} className="px-3 py-2.5 bg-brand-ink text-white text-xs font-bold rounded-sm">Add</button>
                </div>
            </Field>

            <Field label="Vídeo do produto (opcional)" hint="Cole uma URL (YouTube, Instagram, MP4) OU envie um arquivo da galeria/computador (máx 100MB).">
                <div className="flex flex-col sm:flex-row gap-2">
                    <input
                        data-testid="form-video"
                        className={input + " flex-1"}
                        placeholder="https://youtube.com/... ou cole link do vídeo"
                        value={form.video_url}
                        onChange={(e) => update("video_url", e.target.value)}
                    />
                    <label className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-brand-ink text-white text-xs font-bold uppercase tracking-wider rounded-sm cursor-pointer hover:bg-brand-dark whitespace-nowrap">
                        <Upload className="w-4 h-4" />
                        {uploadingVideo ? "Enviando..." : "Escolher vídeo (galeria/arquivo)"}
                        <input
                            type="file"
                            accept="video/*"
                            className="hidden"
                            onChange={uploadVideo}
                            data-testid="form-video-upload"
                            disabled={uploadingVideo}
                        />
                    </label>
                </div>
                {form.video_url && (
                    <div className="mt-2 flex items-center gap-2 text-xs text-brand-inkSoft">
                        <span className="truncate">Vídeo atual: {form.video_url.length > 60 ? form.video_url.slice(0, 60) + "…" : form.video_url}</span>
                        <button type="button" onClick={() => update("video_url", "")} className="text-brand-error font-semibold hover:underline flex-shrink-0">Remover</button>
                    </div>
                )}
            </Field>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Avaliação (0-5)"><input data-testid="form-rating" type="number" step="0.1" min="0" max="5" className={input} value={form.rating} onChange={(e) => update("rating", e.target.value)} /></Field>
                <Field label="Nº avaliações"><input data-testid="form-reviews" type="number" className={input} value={form.reviews_count} onChange={(e) => update("reviews_count", e.target.value)} /></Field>
            </div>

            <Field label="Badges (NOVO, MAIS VENDIDO, OFERTA — vírgula)"><input data-testid="form-badges" className={input} value={Array.isArray(form.badges) ? form.badges.join(", ") : form.badges} onChange={(e) => update("badges", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} /></Field>

            <div className="border-2 border-brand-gold/30 bg-brand-bg/50 rounded-sm p-4">
                <div className="text-xs font-bold uppercase tracking-wider text-brand-gold mb-2">Destaques na vitrine</div>
                <p className="text-[11px] text-brand-inkSoft mb-3">Marque onde esse produto deve aparecer na home da loja. Pode marcar mais de uma.</p>
                <div className="flex flex-wrap gap-4">
                    <label className="inline-flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" data-testid="form-active" className="w-4 h-4" checked={form.is_active} onChange={(e) => update("is_active", e.target.checked)} /> Ativo na loja</label>
                    <label className="inline-flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" data-testid="form-featured" className="w-4 h-4" checked={form.is_featured} onChange={(e) => update("is_featured", e.target.checked)} /> Destaque</label>
                    <label className="inline-flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" data-testid="form-bestseller" className="w-4 h-4" checked={form.is_bestseller} onChange={(e) => update("is_bestseller", e.target.checked)} /> Mais vendido (aparece na seção "Mais vendidos")</label>
                    <label className="inline-flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" data-testid="form-new" className="w-4 h-4" checked={form.is_new} onChange={(e) => update("is_new", e.target.checked)} /> Novidade (aparece na seção "Novidades")</label>
                </div>
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t border-brand-border">
                <button type="button" onClick={onCancel} className="px-5 py-2.5 text-sm font-semibold text-brand-ink hover:bg-brand-bg rounded-sm">Cancelar</button>
                <button type="submit" data-testid="form-save" className="px-6 py-2.5 bg-brand-ink text-white text-sm font-semibold rounded-sm hover:bg-brand-dark">Salvar</button>
            </div>
        </form>
    );
};

const Products = () => {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);

    const load = () => {
        Promise.all([api.get("/admin/products"), api.get("/admin/categories")])
            .then(([p, c]) => { setProducts(p.data); setCategories(c.data); });
    };
    useEffect(() => { load(); }, []);

    const save = async (data) => {
        try {
            if (editing) {
                await api.put(`/admin/products/${editing.id}`, data);
                toast.success("Atualizado");
            } else {
                await api.post("/admin/products", data);
                toast.success("Criado");
            }
            setOpen(false);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erro");
        }
    };

    const remove = async (p) => {
        if (!window.confirm(`Excluir "${p.name}"?`)) return;
        await api.delete(`/admin/products/${p.id}`);
        toast.success("Excluído");
        load();
    };

    const toggleActive = async (p) => {
        await api.put(`/admin/products/${p.id}`, { is_active: !p.is_active });
        load();
    };

    const toggleFlag = async (p, field) => {
        try {
            await api.put(`/admin/products/${p.id}`, { [field]: !p[field] });
            const labels = { is_featured: "Destaque", is_bestseller: "Mais vendido", is_new: "Novidade" };
            toast.success(`${labels[field]}: ${!p[field] ? "ativada" : "desativada"}`);
            load();
        } catch (e) {
            toast.error("Erro ao atualizar");
        }
    };

    return (
        <div data-testid="admin-products-page" className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-brand-gold mb-1">Catálogo</div>
                    <h1 className="font-heading font-bold text-3xl text-brand-ink">Produtos</h1>
                    <p className="text-sm text-brand-inkSoft mt-1">{products.length} cadastrado{products.length !== 1 ? "s" : ""} · {products.filter(p => p.is_active).length} ativo{products.filter(p => p.is_active).length !== 1 ? "s" : ""}</p>
                </div>
                <button onClick={() => { setEditing(null); setOpen(true); }} data-testid="admin-create-product" className="inline-flex items-center justify-center gap-2 bg-brand-ink text-white hover:bg-brand-dark px-5 py-3 text-sm font-semibold rounded-sm">
                    <Plus className="w-4 h-4" /> Novo produto
                </button>
            </div>

            <div className="bg-white border border-brand-border rounded-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-brand-bg border-b border-brand-border">
                            <tr className="text-left text-xs font-bold uppercase tracking-wider text-brand-inkSoft">
                                <th className="px-4 py-3">Produto</th>
                                <th className="px-4 py-3 hidden md:table-cell">Categoria</th>
                                <th className="px-4 py-3">Preço</th>
                                <th className="px-4 py-3 hidden lg:table-cell">Destaques (clique p/ ativar)</th>
                                <th className="px-4 py-3 hidden sm:table-cell">Status</th>
                                <th className="px-4 py-3 text-right">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {products.map((p) => (
                                <tr key={p.id} className={`border-b border-brand-border last:border-0 hover:bg-brand-bg/40 ${!p.is_active ? "opacity-50" : ""}`} data-testid={`prod-row-${p.slug}`}>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-3">
                                            <div className="w-12 h-12 bg-brand-bg rounded-sm overflow-hidden flex items-center justify-center">
                                                {p.images?.[0] ? <img src={p.images[0]} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" /> : <ImageIcon className="w-4 h-4 text-brand-inkSoft" />}
                                            </div>
                                            <div className="min-w-0">
                                                <div className="font-semibold text-brand-ink truncate">{p.name}</div>
                                                <div className="text-xs text-brand-inkSoft">{p.slug}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 hidden md:table-cell capitalize text-brand-inkSoft">{p.category_slug}</td>
                                    <td className="px-4 py-3 font-semibold">{fmtPrice(p.price)}</td>
                                    <td className="px-4 py-3 hidden lg:table-cell">
                                        <div className="flex gap-1 flex-wrap">
                                            <button
                                                type="button"
                                                onClick={() => toggleFlag(p, "is_new")}
                                                data-testid={`flag-new-${p.slug}`}
                                                title="Clique para marcar/desmarcar como Novidade"
                                                className={`text-[10px] font-bold px-2 py-0.5 rounded-sm border transition-colors ${p.is_new ? "bg-brand-success text-white border-brand-success" : "bg-white text-brand-inkSoft border-brand-border hover:border-brand-success hover:text-brand-success"}`}
                                            >
                                                NOVO
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => toggleFlag(p, "is_bestseller")}
                                                data-testid={`flag-bestseller-${p.slug}`}
                                                title="Clique para marcar/desmarcar como Mais vendido"
                                                className={`text-[10px] font-bold px-2 py-0.5 rounded-sm border transition-colors ${p.is_bestseller ? "bg-brand-gold text-white border-brand-gold" : "bg-white text-brand-inkSoft border-brand-border hover:border-brand-gold hover:text-brand-gold"}`}
                                            >
                                                MAIS VENDIDO
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => toggleFlag(p, "is_featured")}
                                                data-testid={`flag-featured-${p.slug}`}
                                                title="Clique para marcar/desmarcar como Destaque"
                                                className={`text-[10px] font-bold px-2 py-0.5 rounded-sm border transition-colors ${p.is_featured ? "bg-brand-ink text-white border-brand-ink" : "bg-white text-brand-inkSoft border-brand-border hover:border-brand-ink hover:text-brand-ink"}`}
                                            >
                                                DESTAQUE
                                            </button>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 hidden sm:table-cell">
                                        <button onClick={() => toggleActive(p)} data-testid={`toggle-${p.slug}`} className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-sm ${p.is_active ? "text-brand-success bg-brand-success/10" : "text-brand-inkSoft bg-brand-bg"}`}>
                                            {p.is_active ? <><Eye className="w-3 h-3"/> Ativo</> : <><EyeOff className="w-3 h-3"/> Inativo</>}
                                        </button>
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <div className="inline-flex gap-1">
                                            <button onClick={() => { setEditing(p); setOpen(true); }} data-testid={`edit-${p.slug}`} className="w-8 h-8 inline-flex items-center justify-center hover:bg-brand-bg rounded-sm"><Edit2 className="w-4 h-4" /></button>
                                            <button onClick={() => remove(p)} data-testid={`delete-${p.slug}`} className="w-8 h-8 inline-flex items-center justify-center hover:bg-red-50 text-brand-error rounded-sm"><Trash2 className="w-4 h-4" /></button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Editar produto" : "Novo produto"}>
                <ProductForm initial={editing || {}} categories={categories} onSubmit={save} onCancel={() => setOpen(false)} />
            </Modal>
        </div>
    );
};

export default Products;
